import { preventNonNumericInput } from '../../helpers/utils';

export default class QuantitySelector {

  private readonly decrementButton: JQuery;
  protected readonly input: JQuery;
  private readonly incrementButton: JQuery;
  private readonly messageContainer: JQuery;

  constructor(protected container: JQuery) {
    this.decrementButton = container.find('.js-qty-selector-minus');
    this.input = container.find('.js-qty-selector-input');
    this.incrementButton = container.find('.js-qty-selector-plus');
    this.messageContainer = container.find('.js-validationMessage');

    if (this.isAtMinimumValue()) {
      this.decrementButton.prop('disabled', true);
    }
    if (this.input.val() === this.input.data('max')) {
      this.incrementButton.prop('disabled', true);
    }

    this.decrementButton.on('click', () => this.checkQtySelector('minus'));
    this.incrementButton.on('click', () => this.checkQtySelector('plus'));

    this.input.on('keypress', (e: Event) => {
      preventNonNumericInput(e);
    });

    this.input.on('keydown', (e) => {
      if (e.which === 38) { // Up key
        this.checkQtySelector('plus');
      } else if (e.which === 40) { // Down key
        this.checkQtySelector('minus');
      } else if (e.which === 13) {
        this.checkQtySelector('blur');
      }
    });

    this.input.on('blur', () => {
      this.checkQtySelector('blur');
    });

    this.container.data('quantity-selector-instance', this);
  }

  static init(element: JQuery): QuantitySelector | void {
    if (element.data('quantity-selector-instance')) {
      return element.data('quantity-selector') as QuantitySelector;
    }
    return new QuantitySelector(element);
  }

  static initAll(): void {
    $('.js-qty-selector').map((i, el) => QuantitySelector.init($(el)));
  }

  private allowsZeroValue() {
    return this.input.data('allow-zero') === true;
  }

  private isAtMinimumValue() {
    const value = this.input.val();
    if (this.allowsZeroValue()) {
      return value === 0;
    }

    return value === this.input.data('rounding-quantity');
  }

  private updateQtyValue(value: number): void {
    const roundingVal = parseInt(this.input.data('rounding-quantity') as string, 10) || 1;
    const max = this.input.data('max') as number;
    const oldValue = value;
    let newValue = value;

    if (this.input.data('allow-zero') === true && value < 0) {
      newValue = 0;
    } else if (newValue < roundingVal && this.input.data('allow-zero') !== true) {
      newValue = roundingVal;
    } else if (newValue >= max) {
      // eslint-disable-next-line no-mixed-operators
      newValue = max - max % roundingVal;
    } else if (newValue % roundingVal !== 0) {
      newValue = Math.ceil(newValue / roundingVal) * roundingVal > max
        ? Math.floor(newValue / roundingVal) * roundingVal
        : Math.ceil(newValue / roundingVal) * roundingVal;
    }

    this.input.val(newValue);

    // These are left after porting ACC.productDetail.js, not sure what the purpose is
    const addtocartQty = this.container.parents('.addtocart-component').find('#addToCartForm').find('.js-qty-selector-input');
    const configureQty = this.container.parents('.addtocart-component').find('#configureForm').find('.js-qty-selector-input');
    addtocartQty.val(newValue);
    configureQty.val(newValue);

    this.input[0].dispatchEvent(new Event('input', { bubbles: true }));
    this.input[0].dispatchEvent(new Event('change', { bubbles: true }));
    if (newValue !== oldValue) {
      this.showMessageContainer();
    } else {
      this.hideMessageContainer();
    }
  }

  private checkQtySelector(mode: string): void {
    const roundingVal = parseInt(this.input.data('rounding-quantity') as string, 10) || 1;
    let inputVal = parseInt((this.input.val() as string), 10);
    if (Number.isNaN(inputVal)) {
      inputVal = 0;
    }
    const max = this.input.data('max') as number;

    this.incrementButton.prop('disabled', false);
    this.decrementButton.prop('disabled', false);

    if (mode === 'minus') {

      this.updateQtyValue(inputVal - roundingVal);

      if ((inputVal - roundingVal === roundingVal && this.input.data('allow-zero') !== true) || inputVal - roundingVal === 0) {
        this.decrementButton.prop('disabled', 'disabled');
      }

    } else if (mode === 'reset') {
      this.updateQtyValue(roundingVal);
    } else if (mode === 'plus') {

      this.updateQtyValue(inputVal + roundingVal);

      if (inputVal + (roundingVal * 2) > max) {
        this.incrementButton.prop('disabled', 'disabled');
      }

    } else if (mode === 'blur') {
      this.updateQtyValue(inputVal);
      const currVal = parseInt(this.input.val() as string, 10);

      // eslint-disable-next-line no-mixed-operators
      if ((inputVal === roundingVal || inputVal < roundingVal) && this.input.data('allow-zero') !== true || currVal === 0) {
        this.decrementButton.prop('disabled', 'disabled');
      } else if (currVal + roundingVal > max) {
        this.incrementButton.prop('disabled', 'disabled');
      }
    }
  }

  showMessageContainer(): void {
    this.messageContainer.show();
  }

  hideMessageContainer(): void {
    this.messageContainer.hide();
  }

  getInput(): JQuery {
    return this.input;
  }
}
